import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, RefreshCw, X, Loader2, ChevronDown, Settings2, Send, Lock, MessageSquare, Paperclip, Server } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

const inputCls = 'w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-3 py-2.5 text-[var(--text-primary)] text-sm font-[Manrope] placeholder-[var(--text-muted)] focus:outline-none focus:border-[#5865F2]/50';
const labelCls = 'text-xs text-[var(--text-muted)] font-[Manrope] uppercase tracking-widest block mb-1.5';

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return '';
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

const ticketNo = (n) => `#${String(n ?? 0).padStart(4, '0')}`;

function StatusPill({ status }) {
  const open = status === 'open';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium font-[Manrope] px-2 py-0.5 rounded-full border ${
      open ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-zinc-500/10 border-zinc-500/20 text-[var(--text-muted)]'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
      {open ? 'Open' : 'Closed'}
    </span>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-40 ${checked ? 'bg-[#5865F2]' : 'bg-[var(--bg-hover)] border border-[var(--border-md)]'}`}
    >
      <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-5' : 'left-1'}`} />
    </button>
  );
}

function TranscriptModal({ guildId, ticketId, onClose, onCloseTicket }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  const ticketStatus = useRef('open');
  ticketStatus.current = ticket?.status ?? 'open';

  useEffect(() => {
    mounted.current = true;
    setLoading(true);
    const load = () => axios.get(`${API}/api/tickets/${guildId}/${ticketId}`, authHeader())
      .then(r => { if (mounted.current) { setTicket(r.data); setError(null); } })
      .catch(e => mounted.current && setError(e.response?.data?.detail || 'Failed to load transcript.'))
      .finally(() => mounted.current && setLoading(false));
    load();
    // Open tickets are read live from Discord, so keep them in sync while viewing.
    const id = setInterval(() => { if (ticketStatus.current === 'open') load(); }, 5000);
    return () => { mounted.current = false; clearInterval(id); };
  }, [guildId, ticketId]);

  const transcript = ticket?.transcript || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--border)]">
          <div className="min-w-0">
            <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg flex items-center gap-2">
              <MessageSquare size={18} className="text-[#5865F2]" />
              Ticket {ticket ? ticketNo(ticket.number) : ''}
              {ticket && <StatusPill status={ticket.status} />}
            </h2>
            {ticket && (
              <p className="text-[var(--text-muted)] text-xs font-[Manrope] mt-0.5 truncate">
                Opened by {ticket.opener_name} · {fmtTime(ticket.created_at)}
                {ticket.status === 'closed' && ticket.closed_by ? ` · Closed by ${ticket.closed_by}` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {ticket?.status === 'open' && (
              <button
                onClick={() => onCloseTicket(ticket.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-xs font-medium font-[Manrope] transition-colors"
              >
                <Lock size={13} /> Close
              </button>
            )}
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <p className="text-red-400 text-sm font-[Manrope]">{error}</p>
          ) : (
            <>
            {ticket?.subject && (
              <div className="mb-4 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-3">
                <p className="text-[var(--text-muted)] text-[11px] uppercase tracking-widest font-[Manrope] mb-1">Issue</p>
                <p className="text-[var(--text-primary)] text-sm font-semibold font-[Manrope] break-words">{ticket.subject}</p>
                {ticket.description && (
                  <p className="text-[var(--text-secondary)] text-sm font-[Manrope] whitespace-pre-wrap break-words mt-1">{ticket.description}</p>
                )}
              </div>
            )}
            {transcript.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare size={28} className="text-[var(--text-muted)] mx-auto mb-2" />
              <p className="text-[var(--text-muted)] text-sm font-[Manrope]">No messages in this ticket yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {transcript.map((m, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#5865F2]/20 text-[#5865F2] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {(m.author || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[var(--text-primary)] text-sm font-semibold font-[Manrope]">{m.author || 'Unknown'}</span>
                      <span className="text-[var(--text-muted)] text-[11px] font-[Manrope]">{fmtTime(m.timestamp)}</span>
                    </div>
                    {m.content && (
                      <p className="text-[var(--text-secondary)] text-sm font-[Manrope] whitespace-pre-wrap break-words">{m.content}</p>
                    )}
                    {(m.attachments || []).length > 0 && (
                      <div className="flex flex-col gap-1 mt-1">
                        {m.attachments.map((url, j) => (
                          <a
                            key={j}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-[#5865F2] hover:underline break-all"
                          >
                            <Paperclip size={12} className="flex-shrink-0" />
                            {String(url).split('/').pop()?.split('?')[0] || 'attachment'}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function SetupCard({ guildId }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, category_id: '', staff_role_id: '', log_channel_id: '', panel_message: '' });
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [panelChannel, setPanelChannel] = useState('');
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open || loaded || !guildId) return;
    Promise.allSettled([
      axios.get(`${API}/api/tickets/${guildId}/settings`, authHeader()),
      axios.get(`${API}/api/tickets/${guildId}/categories`, authHeader()),
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`, authHeader()),
      axios.get(`${API}/api/announce/channels/${guildId}`, authHeader()),
    ]).then(([s, c, r, ch]) => {
      if (s.status === 'fulfilled') {
        const d = s.value.data || {};
        setSettings({
          enabled: !!d.enabled,
          category_id: d.category_id || '',
          staff_role_id: d.staff_role_id || '',
          log_channel_id: d.log_channel_id || '',
          panel_message: d.panel_message || '',
        });
      } else {
        toast.error('Failed to load ticket settings');
      }
      if (c.status === 'fulfilled') setCategories(c.value.data.categories || []);
      if (r.status === 'fulfilled') setRoles(r.value.data.roles || []);
      if (ch.status === 'fulfilled') {
        const chans = ch.value.data.channels || [];
        setChannels(chans);
        if (chans.length > 0) setPanelChannel(chans[0].id);
      }
      setLoaded(true);
    });
  }, [open, guildId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLoaded(false); }, [guildId]);

  const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/tickets/${guildId}/settings`, {
        ...settings,
        category_id: settings.category_id || null,
        staff_role_id: settings.staff_role_id || null,
        log_channel_id: settings.log_channel_id || null,
      }, authHeader());
      toast.success('Ticket settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const postPanel = async () => {
    if (!panelChannel) return;
    setPosting(true);
    try {
      await axios.post(`${API}/api/tickets/${guildId}/panel`, { channel_id: panelChannel }, authHeader());
      toast.success('Ticket panel posted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post panel');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="flex items-center gap-2 text-[var(--text-primary)] font-semibold font-[Outfit]">
          <Settings2 size={16} className="text-[#5865F2]" /> Setup
        </span>
        <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-[var(--border)] pt-4">
              {!loaded ? (
                <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm py-2"><Loader2 size={14} className="animate-spin" /> Loading settings...</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope]">Enable tickets</p>
                      <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Let members open private support tickets</p>
                    </div>
                    <Toggle checked={settings.enabled} onChange={v => set('enabled', v)} />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Ticket category</label>
                      <select value={settings.category_id} onChange={e => set('category_id', e.target.value)} className={inputCls}>
                        <option value="">— Select a category —</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Staff role</label>
                      <select value={settings.staff_role_id} onChange={e => set('staff_role_id', e.target.value)} className={inputCls}>
                        <option value="">— Select a role —</option>
                        {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Staff alerts &amp; log channel (optional)</label>
                      <select value={settings.log_channel_id} onChange={e => set('log_channel_id', e.target.value)} className={inputCls}>
                        <option value="">None</option>
                        {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Panel message</label>
                    <textarea
                      value={settings.panel_message}
                      onChange={e => set('panel_message', e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="Need help? Click the button below to open a ticket."
                      className={`${inputCls} resize-y`}
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={save}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5865F2] hover:bg-[#5865F2]/80 text-white text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40"
                    >
                      {saving && <Loader2 size={14} className="animate-spin" />} Save settings
                    </button>
                  </div>

                  <div className="border-t border-[var(--border)] pt-4">
                    <label className={labelCls}>Post ticket panel</label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select value={panelChannel} onChange={e => setPanelChannel(e.target.value)} className={inputCls}>
                        {channels.length === 0 && <option value="">No channels available</option>}
                        {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                      </select>
                      <button
                        onClick={postPanel}
                        disabled={posting || !panelChannel}
                        className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border-md)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] text-sm font-medium font-[Manrope] transition-colors disabled:opacity-40 flex-shrink-0"
                      >
                        {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Post panel
                      </button>
                    </div>
                    <p className="text-[var(--text-muted)] text-xs font-[Manrope] mt-2">
                      Tip: you can also run <code className="px-1 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-secondary)]">/ticket_panel</code> in Discord.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

export default function Tickets() {
  const { selectedGuildId, selectedGuild } = useGuild();
  const isStaff = !!selectedGuild?.is_staff;
  const isGlobal = !!selectedGuild?.is_global;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('open');
  const [viewing, setViewing] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/api/tickets/${selectedGuildId}?status=all`, authHeader());
      setTickets(r.data.tickets || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId, isGlobal]);

  useEffect(() => {
    setLoading(true);
    fetchTickets();
    const id = setInterval(fetchTickets, 30000);
    return () => clearInterval(id);
  }, [selectedGuildId, isGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeTicket = async (id) => {
    if (!window.confirm('Close this ticket? The channel will be archived.')) return;
    try {
      await axios.post(`${API}/api/tickets/${selectedGuildId}/${id}/close`, {}, authHeader());
      toast.success('Ticket closed');
      setViewing(null);
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close ticket');
    }
  };

  const counts = {
    open: tickets.filter(t => t.status === 'open').length,
    closed: tickets.filter(t => t.status !== 'open').length,
    all: tickets.length,
  };
  const visible = tab === 'all' ? tickets : tickets.filter(t => (tab === 'open' ? t.status === 'open' : t.status !== 'open'));

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="tickets-main">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-[Outfit] text-[var(--text-primary)] flex items-center gap-3">
              <LifeBuoy size={28} className="text-[#5865F2]" />
              Tickets
            </h1>
            <p className="text-[var(--text-muted)] text-sm mt-1 font-[Manrope]">Support tickets opened by your members</p>
          </div>
          {!isGlobal && (
            <button
              onClick={fetchTickets}
              className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          )}
        </div>

        {isGlobal ? (
          <div className="max-w-3xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
            <Server size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-[Manrope] text-sm">Select a specific server to manage tickets</p>
          </div>
        ) : (
          <div className="max-w-3xl space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm font-[Manrope]">{error}</div>
            )}

            {isStaff && <SetupCard guildId={selectedGuildId} />}

            <div className="flex items-center gap-1 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-1 w-fit">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium font-[Manrope] transition-colors flex items-center gap-2 ${
                    tab === t.key ? 'bg-[#5865F2] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {t.label}
                  <span className={`text-[11px] px-1.5 rounded-full ${tab === t.key ? 'bg-white/20' : 'bg-[var(--bg-hover)] text-[var(--text-muted)]'}`}>{counts[t.key]}</span>
                </button>
              ))}
            </div>

            {visible.length === 0 ? (
              <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-8 text-center">
                <LifeBuoy size={32} className="text-[var(--text-muted)] mx-auto mb-3" />
                <p className="text-[var(--text-muted)] font-[Manrope] text-sm">No {tab === 'all' ? '' : `${tab} `}tickets</p>
                <p className="text-[var(--text-muted)] font-[Manrope] text-xs mt-1">Members can open one from a ticket panel in Discord</p>
              </div>
            ) : (
              <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl divide-y divide-[var(--border)] overflow-hidden">
                {visible.map(t => (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setViewing(t.id)}
                    onKeyDown={e => { if (e.key === 'Enter') setViewing(t.id); }}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  >
                    <span className="text-[var(--text-muted)] text-sm font-mono flex-shrink-0">{ticketNo(t.number)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope] truncate">
                        {t.subject || t.opener_name || t.opener_id}
                      </p>
                      <p className="text-[var(--text-muted)] text-xs font-[Manrope] truncate">
                        {t.subject ? `${t.opener_name || t.opener_id} · ` : ''}Opened {timeAgo(t.created_at)}
                        {t.status !== 'open' && typeof t.message_count === 'number' ? ` · ${t.message_count} message${t.message_count !== 1 ? 's' : ''}` : ''}
                        {t.status !== 'open' && t.closed_by ? ` · Closed by ${t.closed_by}` : ''}
                      </p>
                    </div>
                    <StatusPill status={t.status} />
                    {t.status === 'open' && (
                      <button
                        onClick={e => { e.stopPropagation(); closeTicket(t.id); }}
                        className="flex-shrink-0 flex items-center gap-1 text-[var(--text-muted)] hover:text-red-400 transition-colors px-2 py-1 rounded-lg hover:bg-red-500/10 text-xs font-[Manrope]"
                        title="Close ticket"
                      >
                        <Lock size={13} /> Close
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <AnimatePresence>
        {viewing && (
          <TranscriptModal
            guildId={selectedGuildId}
            ticketId={viewing}
            onClose={() => setViewing(null)}
            onCloseTicket={closeTicket}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
